import React, { useState } from "react";
import { MemberRegistrationForm } from "../../types";
import { getMembershipLabel, getMembershipPrice } from "../../utils/membership";
import { toTitleCase } from "../../utils/textNormalization";

interface SponsorDetailsStepProps {
  formData: MemberRegistrationForm;
  updateFormData: (updates: Partial<MemberRegistrationForm>) => void;
  onNext: () => void;
  onBack: () => void;
}

const SponsorDetailsStep: React.FC<SponsorDetailsStepProps> = ({
  formData,
  updateFormData,
  onNext,
  onBack,
}) => {
  const [error, setError] = useState("");

  const handleNext = () => {
    if (!formData.businessName?.trim()) {
      setError("Business name is required");
      return;
    }
    setError("");
    onNext();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Sponsor Details</h2>
      <p className="text-gray-600 mb-6">
        {getMembershipLabel(formData.membershipType)} — $
        {getMembershipPrice(formData.membershipType).toLocaleString()}
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Business Name *
          </label>
          <input
            type="text"
            value={formData.businessName || ""}
            onChange={(e) => {
              updateFormData({ businessName: toTitleCase(e.target.value), isSponsor: true });
              if (error) setError("");
            }}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue ${
              error ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Enter your business name"
          />
          {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
        <p className="font-medium mb-2">
          Your {formData.sponsorTier?.toLowerCase()} sponsor membership
          includes:
        </p>
        <ul className="space-y-1">
          <li>✓ Free family membership — 2 adults, 3 juniors</li>
          <li>
            ✓ Business logo on club event signage and promotional materials
          </li>
          <li>✓ Logo on club shirt</li>
          <li>✓ Invitation to our annual Sponsor Appreciation Night</li>
          <li>✓ Certificate of appreciation</li>
          {(formData.sponsorTier === "GOLD" ||
            formData.sponsorTier === "SILVER") && (
            <>
              <li>✓ Mention in all club newsletters throughout the year</li>
              <li>
                ✓ Verbal acknowledgement at major club events and competitions
              </li>
              <li>
                ✓ Opportunity for business merch to be worn at one competition
                day
              </li>
            </>
          )}
          {formData.sponsorTier === "GOLD" && (
            <>
              <li>✓ Business banner displayed at all monthly competitions</li>
              <li>✓ Dedicated social media post highlighting your business</li>
              <li>
                ✓ Priority placement on club signage and digital platforms
              </li>
            </>
          )}
        </ul>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="px-6 py-2 bg-fishing-blue hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default SponsorDetailsStep;
